import React, { useEffect, useState } from 'react';
import { StyledFilter } from './style';
import { Form } from 'antd';
import { Button, Tag } from 'ui';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import PaginationSelect from '../../Selects/PaginationSelect';
import { BillingStatus } from '../../../enums/order-status';
import FilterTag from './FilterTag';
import { parseFilterDefaults } from 'utils/filterHelper';

interface FilterProps {
  children: React.ReactNode;
  onFinish: (v: any) => void;
  totalRecord?: number;
  staticFilter?: any;
  customParse?: Record<string, (v: any) => any>;
  isPagination?: boolean;
  isLabelVisible?: boolean;
  defaultFilterParam?: Record<string, any>;
  reloadFilterTag?: number;
  tagLabelPrefix?: string;
  tagEnumPrefixes?: Record<string, string>;
}

enum FilterKeys {
  Pagination = 'page_size',
  Confirmed = 'confirmed',
  Emarket = 'emarket',
}

const Filter = ({
  children,
  onFinish,
  totalRecord,
  staticFilter,
  customParse,
  isPagination = true,
  isLabelVisible = false,
  defaultFilterParam,
  reloadFilterTag,
  tagLabelPrefix,
  tagEnumPrefixes,
}: FilterProps) => {
  const [filterProps, setFilterProps] = useState(defaultFilterParam);
  const intl = useIntl();
  const [form] = Form.useForm();

  useEffect(() => {
    if (!defaultFilterParam) return;
    const initialValues = parseFilterDefaults(defaultFilterParam);
    form.setFieldsValue(initialValues);
  }, [defaultFilterParam, form]);

  useEffect(() => {
    if (reloadFilterTag && reloadFilterTag > 0) {
      form.resetFields();
      if (defaultFilterParam) {
        const initialValues = parseFilterDefaults(defaultFilterParam);
        setFilterProps(initialValues);
        form.setFieldsValue(initialValues);
      } else {
        setFilterProps({});
      }
    }
  }, [reloadFilterTag, form, defaultFilterParam]);

  const ENUM_TRANSLATION_FIELD = [
    'status',
    'role',
    'delivered',
    'delivery_type',
    'transaction_type',
    'payment_status',
  ];

  const hasRealValue = (v: unknown): boolean => {
    if (v === 0) return true;
    if (v === false) return true;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'string') return v.trim() !== '';
    return v != null;
  };

  const onFinishInner = (value: any) => {
    let parseValues: Record<string, any> = {};
    let localStatic = { ...staticFilter };

    Object.entries(value).forEach(([key, val]: any) => {
      if (customParse?.[key]) {
        if (key === 'month') {
          localStatic = { ...localStatic, [key]: val?.format('M') };
          parseValues = { ...parseValues, [key]: customParse[key](val) };
          return;
        }
        parseValues = { ...parseValues, [key]: customParse[key](val) };
        localStatic = { ...localStatic, [key]: dayjs.isDayjs(val) ? customParse[key](val) : val };
        return;
      }

      if (dayjs.isDayjs(val)) {
        parseValues = { ...parseValues, [key]: val.format('DD.MM.YYYY') };
        return;
      }

      if (
        typeof val === 'object' &&
        val !== null &&
        'label' in val &&
        ('value' in val || 'key' in val)
      ) {
        const id = (val as any).value ?? (val as any).key;
        const label = (val as any).label;
        parseValues = { ...parseValues, [key]: label };
        localStatic = { ...localStatic, [key]: id };
        return;
      }

      parseValues = { ...parseValues, [key]: val };
    });

    setFilterProps(parseValues);
    onFinish({ ...parseValues, ...localStatic });
  };

  const onClear = () => {
    onFinishInner({});
    form.resetFields();
  };

  const onClearInner = (key: string) => {
    form.setFieldValue(key, undefined);
    form.submit();
  };

  return (
    <StyledFilter className={'styled-filter'}>
      <div className="main-filter">
        <Form form={form} layout="vertical" onFinish={onFinishInner}>
          <div className="horizontal-form">
            {children}
            {isPagination && (
              <span className="pagination">
                <PaginationSelect
                  label={intl.formatMessage({ id: 'general.show' })}
                  name="page_size"
                  className="pagination"
                  defaultValue={25}
                  onChange={(sizePerPage: number) => {
                    form.setFieldValue('page_size', sizePerPage);
                    form.submit();
                  }}
                />
              </span>
            )}
          </div>
        </Form>
      </div>

      <div className="filter-container">
        <div className="tag-list">
          {filterProps &&
            Object.entries(filterProps).map((entry: any, index) => {
              if (
                !hasRealValue(entry?.[1]) ||
                entry?.[0] === FilterKeys.Pagination
              ) {
                return null;
              }

              if (entry?.[0] === 'billing_status') {
                switch (entry?.[1]) {
                  case BillingStatus.NOT_PAID:
                    entry[1] = intl.formatMessage({ id: 'general.not_paid' });
                    break;
                  case BillingStatus.PAID:
                    entry[1] = intl.formatMessage({ id: 'general.paid' });
                    break;
                  case BillingStatus.CANCELED:
                    entry[1] = intl.formatMessage({ id: 'general.canceled' });
                    break;
                  case BillingStatus.POST_PAID:
                    entry[1] = intl.formatMessage({ id: 'general.post_paid' });
                    break;
                }
              }

              if (ENUM_TRANSLATION_FIELD.includes(entry?.[0])) {
                const values = Array.isArray(entry[1]) ? entry[1] : [entry[1]];
                const customEnumPrefix = tagEnumPrefixes?.[entry[0]];
                const translatedValues = values.map((value) => {
                  if (customEnumPrefix) {
                    return intl.formatMessage({
                      id: `${customEnumPrefix}.${String(value)}`,
                    });
                  }
                  return intl.formatMessage({
                    id: 'enum.' + String(value).toUpperCase(),
                  });
                });

                if (translatedValues.length > 0) {
                  const label = isLabelVisible
                    ? `${intl.formatMessage({
                        id: 'filter.' + entry[0],
                      })}: `
                    : '';
                  const valueText = translatedValues.join(', ');

                  return (
                    <Tag
                      key={`tag-${entry[0]}`}
                      closable
                      bordered={false}
                      onClose={() => onClearInner(entry[0])}
                    >
                      {label}
                      {valueText}
                    </Tag>
                  );
                }
                return null;
              }

              if (entry?.[0] === 'year' && dayjs.isDayjs(entry?.[1])) {
                const label = isLabelVisible
                  ? `${intl.formatMessage({ id: 'filter.year' })}: `
                  : '';
                return (
                  <Tag
                    key={`tag-${index}`}
                    closable
                    bordered={false}
                    onClose={() => onClearInner(entry[0])}
                  >
                    {label}{entry[1].year()}
                  </Tag>
                );
              }

              if (entry?.[0] === 'month' && dayjs.isDayjs(entry?.[1])) {
                const label = isLabelVisible
                  ? `${intl.formatMessage({ id: 'filter.month' })}: `
                  : '';
                return (
                  <Tag
                    key={`tag-${index}`}
                    closable
                    bordered={false}
                    onClose={() => onClearInner(entry[0])}
                  >
                    {label}{entry[1].format('MMMM')}
                  </Tag>
                );
              }

              if (dayjs.isDayjs(entry?.[1])) {
                return (
                  <Tag
                    key={`tag-${index}`}
                    closable
                    bordered={false}
                    onClose={() => onClearInner(entry[0])}
                  >
                    {entry?.[1].format('DD.MM.YYYY')}
                  </Tag>
                );
              }

              if (
                typeof entry?.[1] === 'boolean' ||
                entry?.[1] === 'true' ||
                entry?.[1] === 'false'
              ) {
                if (entry?.[0] === FilterKeys.Emarket) {
                } else {
                  const isTrue = entry?.[1] === true || entry?.[1] === 'true';

                  const label = isLabelVisible
                    ? `${intl.formatMessage({
                        id: 'filter.' + entry[0],
                      })}: `
                    : '';

                  return (
                    <Tag
                      key={`tag-${index}`}
                      closable
                      bordered={false}
                      onClose={() => onClearInner(entry[0])}
                    >
                      {label}
                      {intl.formatMessage({
                        id: isTrue ? 'general.yes' : 'general.no',
                      })}
                    </Tag>
                  );
                }
              }

              if (entry?.[0] === FilterKeys.Confirmed) {
                return (
                  <Tag
                    key={`tag-${index}`}
                    closable
                    bordered={false}
                    onClose={() => onClearInner(entry[0])}
                  >
                    {intl.formatMessage({
                      id: 'general.' + (entry[1] ? 'yes' : 'no'),
                    })}
                  </Tag>
                );
              }

              if (entry?.[0] === 'public') {
                const label =
                  entry?.[1] === '1' || entry?.[1] === 1 || entry?.[1] === true
                    ? intl.formatMessage({ id: 'general.yes' })
                    : intl.formatMessage({ id: 'general.no' });
                return (
                  <Tag
                    key={`tag-${index}`}
                    closable
                    bordered={false}
                    onClose={() => onClearInner(entry[0])}
                  >
                    {label}
                  </Tag>
                );
              }

              if (entry?.[0] === 'search') {
                const label = entry?.[1];
                return (
                  <Tag
                    key={`tag-${index}`}
                    closable
                    bordered={false}
                    onClose={() => onClearInner(entry[0])}
                  >
                    {label}
                  </Tag>
                );
              }

              if (entry?.[0] === 'page_category') {
                const label = intl.formatMessage({ id: String(entry?.[1]) });
                return (
                  <Tag
                    key={`tag-${index}`}
                    closable
                    bordered={false}
                    onClose={() => onClearInner(entry[0])}
                  >
                    {label}
                  </Tag>
                );
              }

              if (entry?.[0] === 'status') {
                const label = intl.formatMessage({ id: String(entry?.[1]) });
                return (
                  <Tag
                    key={`tag-${index}`}
                    closable
                    bordered={false}
                    onClose={() => onClearInner(entry[0])}
                  >
                    {label}
                  </Tag>
                );
              }

              if (entry?.[0] === FilterKeys.Emarket) {
                const label = entry?.[1]
                  ? intl.formatMessage({ id: 'products.visible' })
                  : intl.formatMessage({ id: 'products.hidden' });

                return (
                  <Tag
                    key={`tag-${index}`}
                    closable
                    bordered={false}
                    onClose={() => onClearInner(entry[0])}
                  >
                    {label}
                  </Tag>
                );
              }

              if (Array.isArray(entry?.[1])) {
                const label = isLabelVisible
                  ? `${intl.formatMessage({
                      id: 'filter.' + entry[0],
                    })}: `
                  : '';
                const valueText = entry[1].join(', ');

                return (
                  <Tag
                    key={`tag-${entry[0]}`}
                    closable
                    bordered={false}
                    onClose={() => onClearInner(entry[0])}
                  >
                    {label}
                    {valueText}
                  </Tag>
                );
              }

              if (typeof entry?.[1] === 'object') {
                return null;
              }

              return (
                <FilterTag
                  key={`tag-${index}`}
                  filterKey={entry[0]}
                  value={entry[1]}
                  onClose={onClearInner}
                  isLabelVisible={isLabelVisible}
                  labelPrefix={tagLabelPrefix}
                  enumPrefixes={tagEnumPrefixes}
                />
              );
            })}
          {filterProps &&
            Object.entries(filterProps)
              .filter(([key]) => key !== FilterKeys.Pagination)
              .some(([, value]) => hasRealValue(value)) && (
              <Button
                type="link"
                label={intl.formatMessage({ id: 'general.delete_filter' })}
                onClick={onClear}
              />
            )}
        </div>
        {Number.isInteger(totalRecord) && (
          <span className="record-count">
            {intl.formatMessage({ id: 'general.count_record' }) + totalRecord}
          </span>
        )}
      </div>
    </StyledFilter>
  );
};

export default Filter;
